import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels} from "@techiediaries/ngx-qrcode";

import { Place } from '@app/modules/model/place';
import { Ticket } from '@app/modules/model/ticket';

import { GenericUtils } from '@app/app.commonutils';
import { platformService } from '@app/modules/common/platform'; 
import { languageSupport, languageService } from '@app/modules/common/language'
import { alertService } from '@app/modules/common/alert';
import { toastService } from '@app/modules/common/toast';
import { printService } from '@app/modules/print';
import { dataService } from '@app/modules/data';
import { syncService } from '@app/modules/sync';
import { deviceService } from '@app/modules/sockets';
import { viewService } from '@app/modules/view';
import { paytefDirectService } from '@app/components/model/ticket/ui-paytef/paytef-direct';

@Component({
    selector: 'ui-ticket-print',
    templateUrl:'./ui-print.html',
    styleUrls: [ 
        './ui-print.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketPrint extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('ticket') _ticket: Ticket = null;
    @Input('tcknfo') _tcknfo: any = null;
    @Input('display') _display: boolean = false;
    @Input('source') _source = null;    // this component source (for debugging purposes)

    get _printing(){
        if (this._ticket){
            return this._ticket.printing;
        }

        return false;
    }

    set _printing(value){
        if (this._ticket){
            this._ticket.printing = value;
            this.change.detectChanges();
        }
    }

    @ViewChild('printable', {static: false}) _printable: ElementRef;
    get PrintableElem(){
        if (this._printable){
            return this._printable.nativeElement;
        }
        return null;
    }

    private get _printers(){
        let _printers = [];

        let _till = this._place.till;   // get this place printer service
        if (!_till){    // get this ticket assigned printer (by its table)
            _till = this._ticket.qrcode ? this._ticket.qrcode.till : null;
        }

        if (_till){
            for(let _printer of _till.printers){
                if (_printer.IsValid){
                    _printers.push(_printer);
                }
            }
        }

        return _printers;
    }

    private _OnOpenDrawer(data){
        let _devices = [];

        let _isauto = data.isauto;  // ticket has been paid
        for(let _printer of this._printers){
            if ((_printer.type == 'BAR') && _isauto){
                _devices.push({
                    device: _printer
                });
            }
        }
    
        setTimeout(async () => {
            let _promises = [];     // send all and wait for all to finish
            for(let _device of _devices){
                _promises.push(this.printer.OpenDrawer(this._source, this._ticket.place, _device.device, this._attributionWaiterId(this._ticket)));
            }
            await Promise.all(_promises);
        }, 0);    
    }

    private async _OnTicketPrint(data){
        // Cross-instance dedup: el editor y el thumb de tab-tickets pueden
        // estar suscritos al MISMO ticket — sin esto los dos imprimirian
        // el receipt. Ticket.DoCommit (y ToPrinter/ToPaytefPrinter/DoPrint)
        // emiten un PrintClaim fresco; el primero que llama take() gana,
        // los demas se saltan. Observers no-printer (race en ui-edit /
        // ui-select) no llaman a take y siguen funcionando como antes.
        if (data?.claim && !data.claim.take()){
            return;
        }

        // ticketReady cubre LogTicketChange (NextInvoice + setLastInvoice
        // + invceno/series asignados) + firma VeriFactu/TicketBAI (QR).
        // commitReady cubre el super.DoCommit en backend. Ambas vienen
        // en el payload desde Ticket.DoCommit y se awaitan DENTRO del
        // setTimeout — el editor cierra al instante en ~10ms.
        //
        // - _waitReady traga el reject (receipt sin QR mejor que nada)
        // - _waitCommit propaga el reject (si el commit falla, no
        //   imprimimos el receipt — mismo comportamiento que .13 donde
        //   las prints solo salian dentro de if(_result))
        const _waitReady = async () => {
            if (data?.ticketReady){
                try { await data.ticketReady; }
                catch(e) {
                    console.error("[PRINT] ticketReady rejected — receipt may render incomplete", e);
                }
            }
        };
        const _waitCommit = async (): Promise<boolean> => {
            if (data?.commitReady){
                try { await data.commitReady; return true; }
                catch(e) {
                    console.warn("[PRINT] commit failed — skipping receipt print", e);
                    return false;
                }
            }
            return true;    // sin commitReady (ej. ToPrinter/manual), no bloquear
        };

        // Handle Paytef terminal print (pure HTML, no ESC/POS)
        if (data.terminal){
            this._printing = true;
            setTimeout(async () => {
                try {
                    await _waitReady();
                    if (!await _waitCommit()){
                        return;     // commit fallo en backend, no imprimir
                    }
                    await this._TicketInfo(true);

                    let _ok = false;
                    if (this._printable){
                        _ok = await this._sendToPaytefPrinter(data.terminal, this._printable.nativeElement);
                    }

                    // Only log the RECEIPT event on a real successful send,
                    // otherwise the activity log fills with false positives
                    // when the WS/terminal is offline.
                    if (_ok){
                        console.log("[PRINT] Register the ticket print event (Paytef terminal)");
                        this.data.LogServerAction('RECEIPT', {
                            place: this._ticket.place,
                            info: this._ticketinfo
                        });
                    }
                    else {
                        console.warn("[PRINT] Paytef terminal print failed — skipping RECEIPT log");
                        this.toast.ShowAlert('danger', this.lang.tr('@paytef_error_no_connection'));
                    }
                }
                finally {
                    this._printing = false;
                }
            }, 0);

            return;
        }

        // Datafono Print button (sin printer/terminal/auto) → local paytef terminal.
        // Auto-prints van por ToPaytefPrinter respetando onpaycash/onpaycard del terminal.
        if (this.view.IsDatafono && !data.printer && !data.terminal && !data.isauto){
            let _till = this._place.till;
            if (!_till){
                _till = this._ticket.qrcode ? this._ticket.qrcode.till : null;
            }
            let _terminals = _till?.payment_terminals?.filter(t => t.status == 'AC' && t.IsPaytef) || [];   // solo PAYTEF imprime recibos (PROSEGUR/CASHLOGY: host es alias del cajon, no IP Paytef)
            let _terminal = _terminals.length > 0 ? _terminals[0] : null;

            this._printing = true;
            setTimeout(async () => {
                try {
                    await _waitReady();
                    if (!await _waitCommit()){
                        return;     // commit fallo en backend, no imprimir
                    }
                    await this._TicketInfo(true);

                    let _printed = false;
                    if (this._printable){
                        _printed = await this._sendToPaytefPrinter(_terminal, this._printable.nativeElement);
                    }

                    // Log RECEIPT solo en print confirmado — si fallo, alerta al cashier.
                    if (_printed){
                        console.log("[PRINT] Register the ticket print event (datáfono mode)");
                        this.data.LogServerAction('RECEIPT', {
                            place: this._ticket.place,
                            info: this._ticketinfo
                        });
                    }
                    else {
                        console.error("[PRINT] Datáfono receipt print failed");
                        this.toast.ShowAlert('danger', this.lang.tr('@paytef_print_failed'));
                    }
                }
                finally {
                    this._printing = false;
                }
            }, 0);
            return;
        }

        // Paytef autoprint vive en PaytefAutoprintOverlay (root-level) — Ticket.DoCommit lo push.

        // Capturamos data y referencias sincronamente antes del setTimeout —
        // el resto (lectura del ticket, calculo de _devices, etc.) corre
        // dentro del setTimeout despues de awaitar la cripto.
        const _ticketRef = this._ticket;
        const _printersRef = this._printers;

        this._printing = true;
        setTimeout(async () => {
            try {
                await _waitReady();
                if (!await _waitCommit()){
                    return;     // commit fallo en backend, no imprimir
                }
                await this._TicketInfo(true);

                let _devices = [];

                // send to specific printer device
                if (data.printer){
                    _devices.push({
                        device: data.printer,
                        ticket: true,
                        drawer: false
                    });
                }

                // send to this place printers
                else {
                    let _isauto = data.isauto;
                    let _drawer = data.drawer;

                    let _skipBarReceipt = this.view.IsDatafono && !!data.paytefAutoprint;

                    // El editor (ui-edit) bindea ui-ticket-print al COPY del
                    // ticket. El payment se setea en _CommitPaidTicket sobre el
                    // ORIGINAL, no el COPY. Sin este fallback, el filtro
                    // oncash/oncard quedaba con payment=null y _devices=[].
                    const _effectivePayment = _ticketRef.payment
                        || (_ticketRef.CopyOf as Ticket)?.payment
                        || null;

                    for(let _printer of _printersRef){
                        let _cashprint = _printer.oncash && (['PAYCASH', 'PAYMIXED'].includes(_effectivePayment));
                        let _cardprint = _printer.oncard && (['PAYCARD', 'PAYMIXED'].includes(_effectivePayment));

                        if (_printer.type == 'BAR'){
                            let _ticket = (!_isauto || _cashprint || _cardprint);
                            if (_skipBarReceipt){
                                _ticket = false;
                            }
                            if (_ticket || _drawer){
                                _devices.push({
                                    device: _printer,
                                    ticket: _ticket,
                                    drawer: _drawer
                                });
                            }
                        }
                    }
                }

                // print to selected printer or the local service printers
                if (data.printer || (_printersRef.length > 0)){
                    if (_devices.length > 0 && this._printable){
                        let _promises = [];
                        for(let _device of _devices){
                            if (_device.drawer){
                                _promises.push(this.printer.OpenDrawer(this._source, _ticketRef.place, _device.device, this._attributionWaiterId(_ticketRef)));
                            }
                            if (_device.ticket){
                                _promises.push(this.printer.SendTicket(this._source, _ticketRef, _device.device, this._printable.nativeElement, false));
                            }
                        }
                        await Promise.all(_promises);
                    }

                    // send the ticket printed event to the activity log
                    console.log("[PRINT] Register the ticket print event");
                    this.data.LogServerAction('RECEIPT', {
                        place: _ticketRef.place,
                        info: this._ticketinfo
                    });
                }

                // no local service: try Paytef terminal printer, then browser / cordova
                else {
                    let _till = this._place.till;
                    if (!_till){
                        _till = _ticketRef.qrcode ? _ticketRef.qrcode.till : null;
                    }
                    let _terminals = _till?.payment_terminals?.filter(t => t.status == 'AC' && t.IsPaytef) || [];   // solo PAYTEF imprime recibos (PROSEGUR/CASHLOGY: host es alias del cajon, no IP Paytef)

                    let _ok = false;

                    if (_terminals.length > 0 && this._printable){
                        _ok = await this._sendToPaytefPrinter(_terminals[0], this._printable.nativeElement);
                    }

                    // Either no terminal was available, or the Paytef send
                    // failed — fall back to browser/Cordova printing so the
                    // user is never left with no receipt and no feedback.
                    // printer.SendTicket has no boolean return, so we treat
                    // "did not throw" as success.
                    if (!_ok && this._printable){
                        try {
                            await this.printer.SendTicket(this._source, _ticketRef, null, this._printable.nativeElement, false);
                            _ok = true;
                        }
                        catch (err){
                            console.warn("[PRINT] SendTicket fallback threw:", err);
                        }
                    }

                    if (_ok){
                        console.log("[PRINT] Register the ticket print event");
                        this.data.LogServerAction('RECEIPT', {
                            place: _ticketRef.place,
                            info: this._ticketinfo
                        });
                    }
                    else {
                        console.warn("[PRINT] No printer path succeeded — skipping RECEIPT log");
                        this.toast.ShowAlert('danger', this.lang.tr('@paytef_error_no_connection'));
                    }
                }
            }
            finally {
                this._printing = false;
            }
        }, 0);
    }

    private async _OnInvoiceSent(email){
        let alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_ticket_sent_invoice'),
            message: this.lang.tr('@place_ticket_sent_message', [ email ]),
            backdropDismiss: true,
            buttons: [{
                text: this.lang.tr('@ok'),
                handler: (data) => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        await alert.present();
    }

    private async _OnInvoiceSend(data){
        if (this._ticket['_mailing']){
            return;     // avoid sending multiple times
        }

        this._ticket['_mailing'] = true;
        this.toast.ShowWait();

        this._printing = true;
        setTimeout(async () => {
            let _doSendUrl = 'printer/service/dosend.php';
            if (this._printable){
                let blob = await this.printer.TicketToPdf(this._ticket, this._printable.nativeElement);
                if (blob){
                    this._printing = false;

                    let _params = {
                        ticket: this._ticket['objid'],
                        lang: this._place.Language,
                        invoice: this._ticket.series + this._ticket.invceno + '.pdf',
                        recipient: this._ticket.invoice.client.email
                    };
    
                    this.sync.DoRequest(_doSendUrl, _params, blob).then(
                    data => {
                        this.toast.HideWait();
    
                        if (data['errorcode'] == 0){
                            this._OnInvoiceSent(this._ticket.invoice.client.email);
                        }
                        else {
                            console.error("[SERVICE] Error [" +  data['errorcode'] + "] in '" + _doSendUrl + "'");
                            this.toast.ShowAlert('danger', this.lang.tr('@service_request_error_' + data['errorcode']));
                        }                     
                    }, error => {
                        this.toast.HideWait();
                        console.error("[REQUEST] Error [" + error.status + "] in http request '" + _doSendUrl + "'");
                    });
                }
                else {
                    this._printing = false;
                    this.toast.HideWait();
                }
            } 
            
            setTimeout(() => {
                this._ticket['_mailing'] = false;
            }, 250);
        }, 0);
    }

    /************************/
    /* STARTS HERE          */
    /************************/

    private _place: Place = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, public platform: platformService, private toast: toastService, private alertCtrl: alertService, private data: dataService, private sync: syncService, private printer: printService, private devsvc: deviceService, private view: viewService, private paytefDirect: paytefDirectService) {
        super(lang, change);
    }
    
    private _subscriptions = [];

    ngOnInit(){
        // nothing to do
    }

    ngOnChanges(){
        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        if (this._ticket){
            this._place = this._ticket.place;
            if (this._place && !this._ticket.IsVolatile){
                this._subscriptions.push(
                    this._place.OnTargetRefresh('PLACE').subscribe(
                    data => {
                        this._TicketInfo();
                    })
                )
            }

            this._TicketInfo();

            this._subscriptions.push(
                this._ticket.OnRefresh.subscribe(
                data => { // refresh ticket information
                    this._TicketInfo();
                })
            )

            this._subscriptions.push(
                this._ticket.OnDrawer.subscribe(
                data => {
                    this._OnOpenDrawer(data);
                })
            )

            this._subscriptions.push(
                this._ticket.OnPrint.subscribe(
                data => {
                    // avoid simultaneous printing by different sources.
                    // El finally garantiza liberar el flag aunque
                    // _OnTicketPrint rechace — sin esto, una excepcion
                    // dejaba __printing=true para siempre y las
                    // siguientes comandas no imprimian.
                    if (!this._ticket['__printing']){
                        this._ticket['__printing'] = true;
                        this._OnTicketPrint(data)
                            .catch(err => console.error("[PRINT] _OnTicketPrint threw", err))
                            .finally(() => {
                                this._ticket['__printing'] = false;
                            });
                    }
                })
            )

            this._subscriptions.push(
                this._ticket.OnEmail.subscribe(
                data => {
                    this._OnInvoiceSend(data);
                })
            )
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    /*********************************/
    /* TICKET INFORMATION            */
    /*********************************/

    // Nombre del camarero del TICKET para la impresión (solo el del ticket, no
    // por línea). Se resuelve SIN traer la fila USER al cliente (no exponemos
    // email/teléfono/hash): el objid viaja en la columna y aquí se casa contra
    // datos YA cargados en el place —`place.waiters` (alias del STAFF de ESTE
    // local) y `place.user` (owner)—. Null si no se puede resolver (p.ej. un
    // camarero ya no presente en el local) o es QR/guest.
    private _WaiterName(): string {
        let _wuser = this._ticket.waiter;
        let _place = this._ticket.place;
        if (!_wuser || !_place){
            return null;
        }

        // Camarero: alias del STAFF de este local (fallback al nombre del user
        // del STAFF, que sí se carga vía la relación STAFF->USER del place).
        for(let _staff of _place.waiters){
            if (_staff.IsValid && _staff.user && (_staff.user.objid == _wuser.objid)){
                return _staff.alias || (_staff.user.fullname || null);
            }
        }

        // Owner (no es STAFF): su nombre desde place.user.
        if (_place.user && (_place.user.objid == _wuser.objid)){
            return _place.user.fullname || null;
        }

        return null;
    }

    // Camarero al que se ATRIBUYE una impresión / apertura de cajón en el informe de
    // actividad: el SELECCIONADO ahora (identificado por PIN) —quien HACE la acción—,
    // que es lo que pide el informe. Si no hay ninguno identificado (lock de camareros
    // desactivado, o CurrentWaiter ya limpio al imprimir tras el cobro), el camarero
    // DEL TICKET (campo crudo Ticket.WaiterId, fiable aunque el hijo User no esté
    // cargado); y si tampoco, null -> data.ts cae a la sesión como siempre.
    // Antes se usaba solo el del ticket y una reimpresión desde la lista salía a
    // nombre del último que editó la comanda, no del camarero que imprimía.
    private _attributionWaiterId(ticket: Ticket): number {
        if (this.data.HasActiveWaiter && this.data.CurrentWaiter && this.data.CurrentWaiter.objid){
            let _sel = Number(this.data.CurrentWaiter.objid);
            if (!isNaN(_sel) && (_sel > 0)){
                return _sel;
            }
        }
        return ticket ? ticket.WaiterId : null;
    }

    private _ticketinfo = null;
    private async _TicketInfo(_toprint = false){
        this._ticketinfo = null;

        if (!this._ticket.IsLoaded && !this._ticket.ToInsert ){
            return null;    // ticket is not ready
        }

        let _ticket = {
            logo: null,

            /* place information */
            place: this._ticket.place.name,
            table: this._ticket.TableNo,    // alias de la mesa si lo tiene, si no el número
            phone: this._ticket.place.phone,
            address: this._ticket.place.address ? this._ticket.place.address.formatted : null,
            invoice: null,
            business: this._ticket.place.business,
            taxid: this._ticket.place.taxid,
            fiscal: this._ticket.place.fiscal ? this._ticket.place.fiscal.formatted : null,

            /* invoice references */
            created_date: null,
            created_ticket: null,
            invoice_date: null,
            invoice_ticket: null,

            /* ticket information */
            date: null,
            time: null,
            order: this._ticket.orderno,
            waiter: this._WaiterName(),     // camarero del ticket (último que editó), NOMBRE para el recibo
            waiterid: this._attributionWaiterId(this._ticket),                         // objid USER: camarero SELECCIONADO (quien imprime) o, si no hay, el del ticket
            ticketid: this._ticket.objid ? Number(this._ticket.objid) : null,          // objid TICKET: red de seguridad, el server resuelve TICKET.waiter (igual que e0)

            comments: this._ticket.comments,
            draft: !(this._ticket.IsActive || this._ticket.IsCancelled) || !this._ticket.IsPaid,
            cancelled: this._ticket.IsCancelled,
            products: [],
            menus: [],
            extras: [],
            offers: [],
            discounts: [],

            /* ticket price information */

            total: this._ticket.price,
            base: 0,
            taxes: 0,
            rates: [],

            /* ticket diners information */
            diners: this._ticket.diners,

            /* ticket payment information */
            showpay: this._ShowPaymentMethod,
            payment: this._ticket.payment,
            given: this._ticket.given,
            return: this._ticket.given - this._ticket.price,
            cash: this._ticket.payAmount('PAYCASH'), 
            card: this._ticket.payAmount('PAYCARD'), 

            /* ticketbai info and code */
            ticketbai: false,
            ticketbai_info: null,
            ticketbai_code: null,

            /* verifactu QR code */
            verifactu: false,
            verifactu_code: null
        };

        this._GetTicketLogo(_ticket);
        if (_toprint){
            await this._GetTicketLogo64(_ticket);
        }

        this._GetTicketInvoice(_ticket);
        this._GetTicketOffers(_ticket);
        this._GetTicketDiscounts(_ticket);
        this._GetTicketItems(_ticket);
        this._GetTicketTaxes(_ticket);
        this._GetTicketExtras(_ticket);
        this._BakeExtrasIntoProducts(_ticket);
        this._GetTicketBai(_ticket);
        this._GetVeriFactu(_ticket);

        this._ticketinfo = _ticket;
        
        this.SetLanguage(this._place.Language).then(
        () => {
            this.change.markForCheck();
        });
    }

    get TicketInfo(){
        if (this._tcknfo != null){
            return this._tcknfo;
        }

        return this._ticketinfo;
    }

    private get _ShowPaymentMethod(){
        if (this._place){
            return !this._place.GetConfigOption('optionHidePaymentMethod', false);
        }
        return true; // Default to show if place is not available
    }

    private async _GetTicketLogo(_ticket){
        let _logo = this._ticket.place.logo

        if (!this._ticket.place.PrintLogo || !this._ticket.place.logo){
            return false;
        }

        _ticket.logo = _logo;
    }

    private async _GetTicketLogo64(_ticket){
        let _logo = this._ticket.place.logo

        if (!this._ticket.place.PrintLogo || !this._ticket.place.logo){
            return false;
        }

        _ticket.logo = this._ticket.place.embeddedlogo;
        if (!_ticket.logo){
            _ticket.logo = await GenericUtils.imageTobase64(_logo, 'image/jpeg', 0.8);
        }
    }

    private _toDateStr(date){
        let _date = '';

        _date += date.getDate() + '/';
        _date += (date.getMonth()+1) + '/';
        _date += date.getFullYear();

        return _date;
    }

    private _toTimeStr(date){
        let _time = '';

        _time += ('0' + date.getHours()).slice(-2) + ':';
        _time += ('0' + date.getMinutes()).slice(-2);

        return _time;
    }

    private _GetTicketInvoice(_ticket){
        let ticket_date = this._ticket.paidon || this._ticket.created;

        _ticket.date = this._toDateStr(ticket_date);
        _ticket.time = this._toTimeStr(ticket_date); 

        // on invoice add the previous information (invoices and rectificative invoices)
        if (this._ticket.invoice && this._ticket.invoice.IsValid){
            let _changes = [ this._ticket.change, null ];
            if (_changes[0]){
                _changes[1] = this._ticket.change.prev;
            }
    
            if (_changes[0] != null){
                _ticket.invoice_date = this._toDateStr(_changes[0].created) + ' ' + this._toTimeStr(_changes[0].created);
                _ticket.invoice_ticket = _changes[0].series + _changes[0].invoice;
            }
            
            if (_changes[1] != null){
                _ticket.created_date = this._toDateStr(_changes[1].created) + ' ' + this._toTimeStr(_changes[1].created); 
                _ticket.created_ticket = _changes[1].series + _changes[1].invoice;
            }

            _ticket.invoice = {
                business: this._ticket.invoice.business,
                taxid: this._ticket.invoice.taxid,
                address: this._ticket.invoice.address,
                postalcode: this._ticket.invoice.postalcode,
                region: this._ticket.invoice.region
            }
        }

        // otherwise this is a simplified ticket (only the ticket information)
        else {
            _ticket.created_date = _ticket.date + ' ' + _ticket.date; 
            _ticket.created_ticket = this._ticket.series + this._ticket.invceno;
        }
    }

    /** Delega en TicketProduct.ActiveDirect (IsIncluded, tolerante a
     *  options extras) — alineado con carrito y cocina. Early-return
     *  null en ExtendOrder para espejar la salida "expandida" (base
     *  + todos los modificadores) de la comanda. */
    private _GetDirect(_ticketproduct){
        if (this._place && this._place.ExtendOrder){
            return null;
        }
        if (!_ticketproduct){
            return null;
        }
        return _ticketproduct.ActiveDirect || null;
    }

    private IsSameProduct(p1, p2){
        if (p1.product != p2.product){
            return false;   // not same product
        }

        if (p1.product.IsWeighted) {
            return false;   // do not join weighted products
        }

        if (p1.status != p2.status){
            return false;   // not same status
        }

        if (p1.charge != p2.charge){
            return false;   // not same amount
        }

        if (p1.market != p2.market){
            return false;   // not same market price
        }
        
        if (('isoffer' in p2) && (!!p1.offer != p2.isoffer)){
            return false;   // not same offer appliance
        }
        
        if (('isfixed' in p2) && (!!p1.fixed != p2.isfixed)){
            return false;   // not same fixed appliance
        }

        if (p1.options.length != p2.options.length){
            return false;   // not same number of options
        }

        for (let _p1option of p1.options){
            let _exists = p2.options.some(
            _p2option => {
                return (_p2option.option == _p1option.option);
            });

            if (!_exists){
                return false;   // option not available in p2
            }
        }

        for (let _p2option of p2.options){
            let _exists = p1.options.some(
                _p1option => {
                    return (_p1option.option == _p2option.option);
                });
    
                if (!_exists){
                    return false;   // option not available in p1
                }
        }    

        return true;
    }

    private _FindOfferProduct(needle, haystack){
        for(let _idx=0; _idx < haystack.length; _idx++){
            let _ticketproduct = haystack[_idx].requested[0];
            if (this.IsSameProduct(needle, _ticketproduct)){
                return haystack[_idx];
            }
        }

        return null;    // not found
    }

    private _GetOfferProducts(_cartoffer){
        let _offerproducts = [];
        for (let _cartproduct of _cartoffer.products){
            let _ticketproducts = [];   // list of requested ticketproducts (active and refund)

            _ticketproducts = _ticketproducts.concat(_cartproduct.active);
            _ticketproducts = _ticketproducts.concat(_cartproduct.refund);

            for (let _ticketproduct of _ticketproducts){
                let _offerproduct = this._FindOfferProduct(_ticketproduct, _offerproducts)
                if (_offerproduct != null){
                    _offerproduct.requested.push(_ticketproduct);
                }
                else {
                    _offerproduct = {
                        product: {
                            name: _ticketproduct.product.name
                        },
                        price: _ticketproduct.OfferPrice,
                        requested: [ _ticketproduct ],
                        size: 0,        // calculated below (number of products)
                        charge: 0,      // calculated below (the price to be charged - without refunds) 
                        total: 0        // calculated below (the price to be charged - with refunds)
                    };

                    // replace the product name with the direct
                    let _direct = this._GetDirect(_ticketproduct);
                    if (_direct){
                        _offerproduct.product.name = _direct.name;
                    }

                    _offerproducts.push(_offerproduct);
                }
            }
        }

        // calculate final values
        for (let _offerproduct of _offerproducts){
            for (let _requested of _offerproduct.requested){
                _offerproduct.total += _requested.charge;
            }

            _offerproduct.total = Math.round(_offerproduct.total *100)/100;
            _offerproduct.size = _offerproduct.requested.length;
            _offerproduct.charge = _offerproduct.size * _offerproduct.price;
            _offerproduct.charge = Math.round(_offerproduct.charge *100)/100;

            delete _offerproduct.requested;     // remove (avoid circular JSON)
        }

        return _offerproducts;
    }

    private _GetTicketOffers(_ticket){
        let _offers = [];

        _offers = _offers.concat(this._ticket.OffersToDeliver);

        let _products = [];
        if (this._place.ExtendTicket){
            _products = _products.concat(this._ticket.ProductsToPrepare);
            _products = _products.concat(this._ticket.ProductsToRefund);
            _products = _products.concat(this._ticket.ProductsDelivered);
            _products = _products.concat(this._ticket.ProductsCancelled);            
        }

        for(let _item of _offers){
            let _itemoffer = _item.offer;

            let _offer = {
                products: [],    // calculated below

                objid: _itemoffer.offer.objid,
                count: _itemoffer.requested.length,
                name: _itemoffer.offer.name,

                fixed: _itemoffer.fixed,
                house: false,   // calculated below 
                price: 0,       // calculated below
                total: 0,       // calculated below
                adjust: 0       // calculated below 
            };

            // display menu items if ticket is extended (always display for offers)
            if (!_itemoffer.offer.ismenu || this._place.ExtendTicket ){
                _offer.products = this._GetOfferProducts(_itemoffer);
                _offer.price = _itemoffer.offer.price;  // this is the single offer price (as configured)
                _offer.total = _itemoffer.offerprice;   // this is the offer price applied to the number of offers included
            }
            else {
                _offer.products = [];
                _offer.price = _itemoffer.singleprice;  // this is the single offer price (with products)
                _offer.total = _itemoffer.totalprice;   // this is the total price to be charged to the number of offers included
            }

            // adjust the diplay price when fixed
            if (_itemoffer.fixed){
                _offer.adjust = 0 - (_itemoffer.totalprice - _itemoffer.chargeprice);
                _offer.house = (_itemoffer.chargeprice == 0);
            }

            // display as offer or as menu (based on offer type)
            if (_itemoffer.offer.ismenu){
                _ticket.menus.push(_offer);
            }
            else {
                _ticket.offers.push(_offer);
            }
        }
    }

    private _GetTicketDiscounts(_ticket){
        let _discounts = [];

        _discounts = _discounts.concat(this._ticket.DiscountsToApply);

        for(let _itemdiscount of _discounts){
            let _discount = {
                objid: _itemdiscount.discount.objid,
                name: _itemdiscount.discount.name,
                percent: _itemdiscount.percnt,
                total: _itemdiscount.charge
            };

            _ticket.discounts.push(_discount);
        }
    }

    neg(price){
        return (price < 0);
    }

    abs(price){
        return Math.abs(price);
    }

    private AddRequested(product, products, offers){
        // do not print separators
        if (product.Separator){
            return;
        }

        // update the offer information
        if (product.isoffer){
            this._OfferAddRequested(product, offers);
        }

        // if same product -> update products
        for(let _product of products){
            if (this.IsSameProduct(product, _product)){
                _product.count += product.product.IsWeighted ? (product.weight/1000) : 1;
                // Acumular el charge REAL en un campo SEPARADO (chargetotal); `charge` se
                // deja intacto como charge POR UNIDAD porque IsSameProduct lo compara para
                // agrupar (si lo mutáramos al total, un 3er ítem idéntico no casaría). Como
                // no se agrupan ponderados y todos comparten charge/unidad, chargetotal pasa
                // a ser count*(charge unidad) = total real de la línea. Sin esto, en líneas de
                // precio FIJO el adjust de _GetTicketItems dejaba el neto (charge+adjust) en el
                // de UNA unidad, descuadrando el desglose y el reparto de suplementos (p.ej.
                // 2 uds a 8€ contaban 8€ en vez de 16€). (Copilot #1808)
                _product.chargetotal += product.charge;

                let _order = product.created.getTime();
                if (_order < _product.order){
                    _product.order = _order;
                }

                return;     // same product found
            }
        }

        // not found -> add to products
        products.push({
            count: product.product.IsWeighted ? (product.weight/1000) : 1,   // this is the first coincidence of this product

            product: product.product,
            // Referencia al TicketProduct real (para leer ActiveDirect/
            // ExtraOptions en _GetTicketItems). IsSameProduct los agrupa,
            // el primero es representante valido.
            ticketproduct: product,
            name: product.product.name,
            type: product.product.type,
            status: product.status,
            charge: product.charge,          // POR UNIDAD (lo usa IsSameProduct para agrupar)
            chargetotal: product.charge,     // acumulado = count*(charge unidad); lo usa _GetTicketItems
            price: product.price,
            offer: product.offer,
            isoffer: !!product.offer,
            fixed: product.fixed ? product.fixedprice : null,
            weight: product.weight,
            market: product.market,
            isfixed: !!product.fixed,
            isweight: product.product.IsWeighted,
            options: product.options,
            order: product.created.getTime()
        })
    }

    private _GetTicketItems(_ticket){
        let _toticket = [];
        let _products = [];
        
        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsToRefund);
        _products = _products.concat(this._ticket.ProductsDelivered);
        _products = _products.concat(this._ticket.ProductsCancelled);

        // Cada ticketproduct se cuenta una sola vez, como cuenta el informe de
        // ingresos (una linea por fila real). Cuando entra un pedido de movil/QR,
        // el estado local puede tener transitoriamente DOS entradas del mismo
        // producto antes de reconciliar con el server: la copia optimista y la
        // fila persistida que vuelve por sync. AddRequested las contaria dos
        // veces e inflaria la cantidad impresa (imprime 3 con 2 reales, total
        // de 2). Se deduplica por identidad de fila usando AMBOS identificadores:
        // refid (_uuid, la clave que usa el server en el do_insert y el storage
        // en _changeKey, se conserva de la copia optimista a la persistida) Y
        // objid (la PK de BD). Se cuenta como duplicado si se repite CUALQUIERA:
        // el _uuid caza la pareja optimista+persistida (mismo _uuid, distinto
        // objid), y el objid caza dos instancias de la misma fila persistida que
        // por rehidratacion/replace acabaron con _uuid distintos. No hay falso
        // merge: dos filas distintas nunca comparten objid (PK) ni _uuid.
        let _seenUuid = new Set<string>();
        let _seenObjid = new Set<string>();
        for(let _item of _products){
            let _itemproduct = _item.product;

            if ((_itemproduct.offer) && (_itemproduct.offer.ismenu)){
                continue;   // skip products included in menus (menus must be displayed as products)
            }

            for (let _requested of _itemproduct.requested){
                let _uuid = _requested.refid;
                let _objid = _requested.objid;

                // Calcular duplicado ANTES de registrar, y registrar SIEMPRE
                // ambos identificadores (aunque se salte). Si no, al saltar una
                // fila persistida por _uuid duplicado su objid no quedaria en el
                // set, y una tercera instancia rehidratada con ese objid pero
                // distinto _uuid se colaria.
                let _dup = _seenUuid.has(_uuid) || (_objid && _seenObjid.has(_objid));

                _seenUuid.add(_uuid);
                if (_objid){
                    _seenObjid.add(_objid);
                }

                if (_dup){
                    continue;   // ya contado (por _uuid o por objid)
                }

                this.AddRequested(_requested, _toticket, _ticket.offers);
            }
        }

        for (let product of _toticket){
            let _product = {
                options: [],

                count: product.count,
                name: product.name,
                refund: (product.status == 'UN'),
                cancel: (product.status == 'DE'),
                offer: product.isoffer,
                fixed: product.isfixed,
                weight: product.isweight,
                price: product.price,
                order: product.order,

                house: false,   // calculated below
                charge: 0,      // calculated below
                adjust: 0       // calculated below
            };       

            if (product.isweight){
                _product.price = Math.round((product.price * 1000 / product.weight) * 100) / 100;
                _product.charge = product.price;
            }
            else {
                _product.charge = Math.round(product.count * Number(product.price) * 100)/100;
            }

            if (product.isfixed){
                // chargetotal = total REAL de la línea (count*charge unidad); product.charge
                // es por unidad y dejaría el neto en 1 ud en líneas agrupadas. (Copilot #1808)
                _product.adjust = 0 - (_product.charge - product.chargetotal);
                _product.house = (product.chargetotal == 0);
            }

            // Con direct: solo extras. Sin direct: todas.
            let _direct = this._GetDirect(product.ticketproduct);
            let _optionsToRender = product.options;
            if (_direct && product.ticketproduct){
                _optionsToRender = product.ticketproduct.ExtraOptions;
            }

            for(let _option of _optionsToRender){
                // Guard: una TicketOption con .option (ProductOption) a null es
                // una opcion huerfana/sin resolver (reconciliacion rota tras
                // reabrir/dividir). Sin esto, generar el ticket revienta con
                // "Cannot read properties of null (reading 'name')" en CADA
                // refresh (Place._DoRefresh -> _TicketInfo -> _GetTicketItems)
                // y bloquea impresion y sync. La omitimos.
                if (!_option || !_option.option){
                    continue;
                }
                let _extra = {
                    count: product.count,
                    name: _option.option.name,
                    price: _option.option.price,
                    total: 0  // calculated below
                }

                _extra.total = _extra.count * Number(_extra.price);
                if (product.isfixed){
                    _extra.total = 0;
                }

                _product.options.push(_extra);
            }

            // replace the product name with the direct
            if (_direct){
                _product.name = _direct.name;
            }

            // Compact: ocultamos options. Extended: mantenemos las extras
            // ya filtradas arriba.
            if (this._place.ExtendTicket == false){
                _product.options = [];
            }

            // replace the product name with the various description
            if (product.product.IsVarious){
                let _description = product.product.description;
                if (_description && (_description.trim().length > 0)){
                    _product['name'] = _description;
                }
            }

            _ticket.products.push(_product);
        }

        _ticket.products.sort((a, b) => {
            return (a.order || 0) - (b.order || 0);
        })
    }

    private _GetTicketTaxes(_ticket){
        let _splited = this._ticket.SplitTaxes;

        let _taxes = 0;
        let _base = 0;
        for (let _rate of _splited){
            _taxes += _rate.taxes;
            _base += _rate.base;
        }

        _ticket.taxes = _taxes;
        _ticket.base = _base;
        _ticket.rates = _splited;
    }

    private _GetTicketExtras(_ticket){
        let _extras = [];

        _extras = _extras.concat(this._ticket.ExtrasToPrepare);

        for(let _itemextra of _extras){
            let _extra = {
                name: _itemextra.extra.name,
                total: _itemextra.charge
            };

            _ticket.extras.push(_extra);
        }
    }

    // "Ocultar suplementos en el ticket": reparte el importe de los suplementos dentro
    // del precio de cada producto (proporcional al importe de cada línea) y vacía la
    // sección de suplementos. SOLO toca el payload de impresión (_ticket.products /
    // _ticket.extras); NO toca el modelo ni base/IVA/total (que salen de SplitTaxes y
    // Ticket.price y ya incluyen el suplemento), así que el total impreso sigue
    // cuadrando. El XML de TicketBAI mantiene la línea del suplemento aparte
    // (divergencia de desglose por línea, aceptable en factura simplificada).
    private _BakeExtrasIntoProducts(_ticket){
        if (!this._place.HideExtras || !_ticket.extras || (_ticket.extras.length == 0)){
            return;
        }

        // El reparto solo es fiable en tickets de PRODUCTOS SIMPLES. Con menús u
        // ofertas, los importes viven en _ticket.menus/_ticket.offers (los productos
        // de menú se omiten en _GetTicketItems) o el marcador de oferta anula el
        // importe de la línea, así que repartir sobre _ticket.products descuadraría el
        // desglose. En esos casos NO horneamos: el suplemento se queda como línea
        // visible (degradación segura, el total sigue cuadrando).
        let _hasMenusOrOffers = (_ticket.menus && (_ticket.menus.length > 0))
            || (_ticket.offers && (_ticket.offers.length > 0))
            || _ticket.products.some(p => p.offer);
        if (_hasMenusOrOffers){
            return;
        }

        let _totalCents = 0;
        for(let _e of _ticket.extras){
            _totalCents += Math.round((Number(_e.total) || 0) * 100);
        }
        if (_totalCents == 0){
            _ticket.extras = [];
            return;
        }

        // Importe NETO de cada línea (charge + adjust): un ítem INVITADO o de precio
        // fijado tiene charge>0 pero un adjust que lo anula (neto 0), así que NO debe
        // recibir suplemento (si no, un invitado pasaría a mostrar importe a pagar).
        let _net = (p) => Number(p.charge) + Number(p.adjust || 0);

        // Líneas cobrables (no devolución/anulación, con NETO > 0) donde repartir.
        let _lines = _ticket.products.filter(p => !p.refund && !p.cancel && (_net(p) > 0));
        let _baseCents = 0;
        for(let _p of _lines){
            _baseCents += Math.round(_net(_p) * 100);
        }
        if ((_lines.length == 0) || (_baseCents == 0)){
            return;   // nada donde repartir: dejar los suplementos como estaban
        }

        // Reparto proporcional al NETO, en céntimos; el remanente va a la última línea
        // para que la suma cuadre EXACTA con productos+suplementos (= total impreso).
        let _assigned = 0;
        for(let _i = 0; _i < _lines.length; _i++){
            let _p = _lines[_i];
            let _lineCents = Math.round(_net(_p) * 100);
            // FLOOR en los repartos no-finales (no round): redondear podría
            // sobre-asignar y dejar al último un remanente NEGATIVO (un suplemento
            // positivo bajaría el precio de esa línea). Con floor, Σ no-finales <=
            // total, así el remanente de la última línea es siempre >= 0.
            let _share = (_i == (_lines.length - 1))
                ? (_totalCents - _assigned)
                : Math.floor(_totalCents * _lineCents / _baseCents);
            _assigned += _share;

            // Sumar al importe BRUTO (charge) preservando el ajuste (adjust): el neto
            // mostrado sube en _share.
            _p.charge = (Math.round(Number(_p.charge) * 100) + _share) / 100;
            // Precio unitario mostrado = importe / cantidad, en AMBOS casos
            // (ponderados: count=kg -> €/kg; no ponderados: €/ud).
            if (_p.count){
                _p.price = Math.round((Number(_p.charge) / _p.count) * 100) / 100;
            }
        }

        // El importe ya está dentro de los productos: ocultar la sección.
        _ticket.extras = [];
    }

    private _indexOfOffer(needle, haystack){
        for(let _idx=0; _idx < haystack.length; _idx++){
            let _current = haystack[_idx];
            if (_current.offers.indexOf(needle) != -1){
                return _idx;
            }
        }
        return -1;
    }
    
    private _indexOfProduct(needle, haystack){
        for(let _idx=0; _idx < haystack.length; _idx++){
            if (haystack[_idx].product['objid'] == needle['objid']){
                return _idx;
            }
        }
        return -1;
    }

    private _OfferAddRequested(_product, _offers){
        if (_product.status == 'UN'){
            return;   // returned products not included in offers
        }

        let _offeridx = this._indexOfOffer(_product.offer, _offers);
        if (_offeridx != -1){
            let _productidx = this._indexOfProduct(_product.product, _offers[_offeridx].products);
            if (_productidx == -1){
                _offers[_offeridx].products.push({ 
                    product: _product.product, 
                    size: 1 
                });
            }
            else {
                _offers[_offeridx].products[_productidx].size++;
            }
        }
    }

    private _GetTicketBai(_ticket){
        _ticket.ticketbai = (this._ticket.ticketbai != null) && (this._ticket.ticketbai.qr != null);
        if (_ticket.ticketbai){
            _ticket.ticketbai_info = this._ticket.ticketbai.id;            
            _ticket.ticketbai_code = {
                _qr_element_type: NgxQrcodeElementTypes.URL,
                _qr_correction_level: NgxQrcodeErrorCorrectionLevels.HIGH,
                _qr_element_value: this._ticket.ticketbai.qr
            };            
        }
    }

    private _GetVeriFactu(_ticket){
        _ticket.verifactu = (this._ticket.verifactu != null) && (this._ticket.verifactu.qr != null);
        if (_ticket.verifactu){
            _ticket.verifactu_code = {
                _qr_element_type: NgxQrcodeElementTypes.URL,
                _qr_correction_level: NgxQrcodeErrorCorrectionLevels.HIGH,
                _qr_element_value: this._ticket.verifactu.qr
            }
        }
    }

    /************************/
    /* PAYTEF PRINT         */
    /************************/

    // Fachada publica para PaytefAutoprintOverlay: wraps _sendToPaytefPrinter + log/toast.
    async sendToPaytefTerminal(terminal): Promise<boolean> {
        if (!this._ticket || !terminal){
            return false;
        }

        this._printing = true;
        let _ok = false;
        try {
            await this._TicketInfo(true);
            try {
                (this.change as any).markForCheck();
                (this.change as any).detectChanges();
            } catch(e) {}

            // rAF wait por si el _TicketInfo aun no acabo de decodear el logo.
            await new Promise<void>(_resolve => requestAnimationFrame(() => requestAnimationFrame(() => _resolve())));

            if (this._printable){
                _ok = await this._sendToPaytefPrinter(terminal, this._printable.nativeElement);
            }

            if (_ok){
                console.log("[PRINT] Register the ticket print event (Paytef autoprint)");
                this.data.LogServerAction('RECEIPT', {
                    place: this._ticket.place,
                    info: this._ticketinfo
                });
            }
            else {
                console.error("[PRINT] Paytef autoprint failed");
                this.toast.ShowAlert('danger', this.lang.tr('@paytef_print_failed'));
            }
        }
        finally {
            this._printing = false;
        }

        return _ok;
    }

    // Fachada publica para el overlay root-level (paytef-autoprint-overlay
    // tambien maneja BAR receipts). Recibe los flags del payload que antes
    // viajaban en el OnPrint emit y dispatcha al BAR printer + drawer
    // segun corresponda. Si la red local no tiene impresoras BAR
    // configuradas, cae al fallback paytef terminal / SendTicket
    // browser/cordova igual que la rama "no local service" del flujo
    // OnPrint original.
    async sendToBARReceipt(data: { isauto: boolean, drawer: boolean, paytefAutoprint: boolean }): Promise<boolean> {
        if (!this._ticket){
            return false;
        }

        this._printing = true;
        let _ok = false;
        try {
            await this._TicketInfo(true);
            try {
                (this.change as any).markForCheck();
                (this.change as any).detectChanges();
            } catch(e) {}

            // rAF wait para que el browser pinte el #printable con los
            // datos completos (igual que paytef autoprint).
            await new Promise<void>(_resolve => requestAnimationFrame(() => requestAnimationFrame(() => _resolve())));

            if (!this._printable){
                return false;
            }

            let _devices = [];

            let _isauto = data.isauto;
            let _drawer = data.drawer;
            let _skipBarReceipt = this.view.IsDatafono && !!data.paytefAutoprint;

            const _effectivePayment = this._ticket.payment
                || (this._ticket.CopyOf as Ticket)?.payment
                || null;

            for(let _printer of this._printers){
                let _cashprint = _printer.oncash && (['PAYCASH', 'PAYMIXED'].includes(_effectivePayment));
                let _cardprint = _printer.oncard && (['PAYCARD', 'PAYMIXED'].includes(_effectivePayment));

                if (_printer.type == 'BAR'){
                    let _ticketprint = (!_isauto || _cashprint || _cardprint);
                    if (_skipBarReceipt){
                        _ticketprint = false;
                    }
                    if (_ticketprint || _drawer){
                        _devices.push({
                            device: _printer,
                            ticket: _ticketprint,
                            drawer: _drawer
                        });
                    }
                }
            }

            if (this._printers.length > 0){
                if (_devices.length > 0){
                    let _promises = [];
                    for(let _device of _devices){
                        if (_device.drawer){
                            _promises.push(this.printer.OpenDrawer(this._source, this._ticket.place, _device.device, this._attributionWaiterId(this._ticket)));
                        }
                        if (_device.ticket){
                            _promises.push(this.printer.SendTicket(this._source, this._ticket, _device.device, this._printable.nativeElement, false));
                        }
                    }
                    await Promise.all(_promises);
                    console.log("[PRINT] Register the ticket print event (BAR overlay)");
                    this.data.LogServerAction('RECEIPT', {
                        place: this._ticket.place,
                        info: this._ticketinfo
                    });
                    _ok = true;
                }
            }
            else {
                // No hay impresoras BAR — fallback a paytef terminal local
                // o SendTicket browser/cordova (rama equivalente al "no
                // local service" del _OnTicketPrint original).
                let _till = this._place.till;
                if (!_till){
                    _till = this._ticket.qrcode ? this._ticket.qrcode.till : null;
                }
                let _terminals = _till?.payment_terminals?.filter(t => t.status == 'AC' && t.IsPaytef) || [];   // solo PAYTEF imprime recibos (PROSEGUR/CASHLOGY: host es alias del cajon, no IP Paytef)

                if (_terminals.length > 0){
                    _ok = await this._sendToPaytefPrinter(_terminals[0], this._printable.nativeElement);
                }

                if (!_ok){
                    try {
                        await this.printer.SendTicket(this._source, this._ticket, null, this._printable.nativeElement, false);
                        _ok = true;
                    } catch(err){
                        console.warn("[PRINT] SendTicket fallback threw:", err);
                    }
                }

                if (_ok){
                    console.log("[PRINT] Register the ticket print event (BAR overlay fallback)");
                    this.data.LogServerAction('RECEIPT', {
                        place: this._ticket.place,
                        info: this._ticketinfo
                    });
                } else {
                    this.toast.ShowAlert('danger', this.lang.tr('@paytef_error_no_connection'));
                }
            }
        }
        finally {
            this._printing = false;
        }

        return _ok;
    }

    private async _sendToPaytefPrinter(terminal, elem): Promise<boolean>{
        let _connection = null;

        try {
            // Paytef terminals have a narrow thermal printer and the
            // default ticket-print.css (sized for 80 mm bar printers)
            // renders a bit small en datafonos. Bump ~50% via font=18
            // (base CSS es 12px, 18 = 150%) y override del QR via un
            // <style> extra inyectado por el parametro `links` — antes
            // usabamos el truco '}' en custom para cerrar body{} y
            // declarar selector externo, lo cual estaba fuertemente
            // acoplado al formato interno de htmlStyleContent.
            // body .qrcode-box img (0,1,2) gana en especificidad al
            // .qrcode-box img (0,1,1) !important del base CSS sin depender
            // del orden de source — el link <style> se inyecta antes del
            // <style> base, asi que necesitamos vencer por especificidad.
            const _extraStyle = '<style>body .qrcode-box img { width: 60mm !important; height: auto !important; }</style>';

            let _html = await this.printer.htmlStyleContent(
                this._ticket.orderno, elem, 'assets/styles/ticket-print.css',
                18, false, [80, 0, 0, 0, 0], null, [ _extraStyle ]
            );

            if (!_html){
                console.warn("[PRINT] No HTML content to send to Paytef printer");
                return false;
            }

            // Datáfono mode — the app lives on the payment terminal
            // itself, so skip the WS envelope and POST the HTML to the
            // local paytef HTTP API directly.
            if (this.view.IsDatafono){
                let _ok = await this.paytefDirect.print(_html, '*');
                if (_ok){
                    console.info("[PRINT] Ticket sent to Paytef terminal printer (datáfono): " + (terminal ? terminal.name : '?'));
                }
                else {
                    console.error("[PRINT] Datáfono paytef print failed");
                }
                return _ok;
            }

            // Use the terminal's owning service, fallback to place till
            let _service = terminal.rasppi;
            if (!_service){
                _service = this._place.till;
                if (!_service){
                    _service = this._ticket.qrcode ? this._ticket.qrcode.till : null;
                }
            }

            if (!_service){
                console.warn("[PRINT] No service available for Paytef print");
                return false;
            }

            // Try local WS first, then remote
            _connection = await this.devsvc.Connect(
                _service.wshost, _service.wsport, _service.name, true
            );

            if (!_connection){
                _connection = await this.devsvc.Connect(
                    null, null, _service.name, false
                );
            }

            if (!_connection){
                console.error("[PRINT] Cannot connect to service for Paytef print");
                return false;
            }

            let _action = JSON.stringify({
                command: 'PRINT',
                host: terminal.host,
                port: 8887,
                pinpad: '*',
                content: _html
            });

            let _response = await _connection.Send('paytef', _action);

            if (_response && _response.exec == 'SUCCESS'){
                console.info("[PRINT] Ticket sent to Paytef terminal printer: " + terminal.name);
                return true;
            }
            else {
                console.error("[PRINT] Failed to print on Paytef terminal: " + terminal.name);
                return false;
            }

        } catch(e) {
            console.error("[PRINT] Paytef print error:", e);
            return false;
        } finally {
            if (_connection){
                _connection.Disconnect();
            }
        }
    }
}
